export type StoredNotifyRequestSummary = {
  publicId: string;
  partySize: number;
  requestedDates: string[];
  timeFrom: string;
  timeUntil: string;
  status: "active" | "fulfilled" | "cancelled" | "expired";
  expiresAt: string;
  createdAt: string;
};

const storagePrefix = "tables:notify-summary:v1:";
const datePattern = /^\d{4}-\d{2}-\d{2}$/;
const timePattern = /^([01]\d|2[0-3]):[0-5]\d$/;
const publicIdPattern = /^[0-9a-f-]{36}$/i;
const statuses = new Set<StoredNotifyRequestSummary["status"]>([
  "active",
  "fulfilled",
  "cancelled",
  "expired",
]);

export function saveNotifyRequestSummary(value: unknown) {
  const summary = parseNotifyRequestSummary(value);
  if (!summary) return false;
  window.localStorage.setItem(
    `${storagePrefix}${summary.publicId}`,
    JSON.stringify(summary),
  );
  return true;
}

export function loadNotifyRequestSummaries(now = new Date()) {
  const summaries: StoredNotifyRequestSummary[] = [];
  for (const key of Object.keys(window.localStorage)) {
    if (!key.startsWith(storagePrefix)) continue;
    let parsed: unknown;
    try {
      parsed = JSON.parse(window.localStorage.getItem(key) ?? "null");
    } catch {
      window.localStorage.removeItem(key);
      continue;
    }
    const summary = parseNotifyRequestSummary(parsed);
    if (!summary) {
      window.localStorage.removeItem(key);
      continue;
    }
    summaries.push(
      summary.status === "active" && Date.parse(summary.expiresAt) <= now.getTime()
        ? { ...summary, status: "expired" }
        : summary,
    );
  }
  return summaries.sort((left, right) =>
    right.createdAt.localeCompare(left.createdAt),
  );
}

export function updateNotifyRequestSummaryStatus(
  publicId: string,
  status: StoredNotifyRequestSummary["status"],
) {
  if (!publicIdPattern.test(publicId) || !statuses.has(status)) return false;
  const key = `${storagePrefix}${publicId}`;
  let parsed: unknown;
  try {
    parsed = JSON.parse(window.localStorage.getItem(key) ?? "null");
  } catch {
    return false;
  }
  const summary = parseNotifyRequestSummary(parsed);
  if (!summary) return false;
  window.localStorage.setItem(key, JSON.stringify({ ...summary, status }));
  return true;
}

function parseNotifyRequestSummary(
  value: unknown,
): StoredNotifyRequestSummary | null {
  if (!value || typeof value !== "object") return null;
  const candidate = value as Partial<StoredNotifyRequestSummary>;
  if (
    typeof candidate.publicId !== "string" ||
    !publicIdPattern.test(candidate.publicId) ||
    !Number.isInteger(candidate.partySize) ||
    (candidate.partySize ?? 0) < 1 ||
    !Array.isArray(candidate.requestedDates) ||
    !candidate.requestedDates.every(
      (date) => typeof date === "string" && datePattern.test(date),
    ) ||
    typeof candidate.timeFrom !== "string" ||
    !timePattern.test(candidate.timeFrom) ||
    typeof candidate.timeUntil !== "string" ||
    !timePattern.test(candidate.timeUntil) ||
    !candidate.status ||
    !statuses.has(candidate.status) ||
    typeof candidate.expiresAt !== "string" ||
    !Number.isFinite(Date.parse(candidate.expiresAt)) ||
    typeof candidate.createdAt !== "string" ||
    !Number.isFinite(Date.parse(candidate.createdAt))
  ) {
    return null;
  }
  return {
    publicId: candidate.publicId.toLowerCase(),
    partySize: candidate.partySize!,
    requestedDates: candidate.requestedDates,
    timeFrom: candidate.timeFrom,
    timeUntil: candidate.timeUntil,
    status: candidate.status,
    expiresAt: candidate.expiresAt,
    createdAt: candidate.createdAt,
  };
}
